'use client';

/**
 * ObeyaCostPlanReviewHeaderCard — identity strip for the review-changes
 * Concertina overlay (mode1_costplan's `overlay_header_card`).
 *
 * "Which Rev, compared against what, how many lines changed" — a pure
 * READING of the compare-store, no control of its own. The "vs" picker
 * (Rev-vs-Rev, not just vs. current live state — see
 * costplan-compare-store.ts's own `compareAgainstRevId` docstring)
 * used to live here (moved from the retired flat
 * `ObeyaCostPlanRevDiffCard`'s header when the Concertina replaced it),
 * then moved AGAIN — real UX feedback: comparing Scenario-vs-Scenario
 * meant opening this overlay just to reach the one control that could
 * change the second side, when the first side could only be picked
 * from a panel elsewhere on the canvas. Both pickers now live together
 * on `obeya_costplan_versions`, always visible, no overlay required to
 * reach either — this header just displays the pair that row picked,
 * so the two never show conflicting controls for the same fact. No
 * close/collapse control either; the overlay shell owns those (same
 * convention as `product_summary_strip`, the PDP overlay's header
 * card). Reads the same compare-store values and `mcp_rev_diff`
 * strategy as `McpWatchWindowCard` — the SAME round-trip as the
 * sibling body card (`obeya_costplan_review_groups`), deduped by React
 * Query on (strategy, params).
 *
 * All copy rides `mcp_rev_diff`'s own `labels` — no hardcoded English.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — obeya_costplan_review_header
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-review-groups-card.tsx — sibling body card
 * @see klyde_ipe/components/canvas/cards/vendors/product-summary-strip-card.tsx — same overlay-header convention
 *
 * @component_role: Card
 * @component_type: Overlay Header Card
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { GitCompare } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMcpRevDiff, useMcpRevList } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';

export function ObeyaCostPlanReviewHeaderCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;

  const diffStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_diff'));
  const listStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_list'));

  const { data: raw } = useMcpRevDiff(diffStrategy, mcpId ?? null, effectiveRevId, effectiveAgainstRevId, lang ?? undefined);
  const diff = raw?.data;

  const { data: revListRaw } = useMcpRevList(listStrategy, mcpId ?? null, lang ?? undefined);
  const revs = revListRaw?.data?.revs ?? [];
  const L = diff?.labels ?? revListRaw?.data?.labels ?? {};
  const comparedRev = revs.find((r) => r.rev_sid === effectiveRevId);
  const comparedRevLine = comparedRev ? (comparedRev.name ? `${comparedRev.published_label} — ${comparedRev.name}` : comparedRev.published_label) : '';

  const againstLabel = effectiveAgainstRevId
    ? (revs.find((r) => r.rev_sid === effectiveAgainstRevId)?.published_label ?? '')
    : (L.compare_vs_current_option ?? '');

  const queryKey = ['strategy-data', diffStrategy, { semantic_id: mcpId ?? '', rev_sid: effectiveRevId ?? '', ...(effectiveAgainstRevId ? { compare_rev_sid: effectiveAgainstRevId } : {}), ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_review_header',
    surface: 'overlay' as const,
    status: (effectiveRevId ? (diff ? 'loaded' : 'loading') : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: effectiveRevId && diffStrategy ? queryKey : null,
    text: diff ? `${L.review_title ?? ''}: ${comparedRevLine} ${L.compare_vs_label ?? 'vs'} ${againstLabel} — ${diff.changed_count} ${L.diff_changed_suffix ?? ''}` : '',
    json: diff ? { rev_sid: diff.rev_sid, compare_rev_sid: diff.compare_rev_sid, changed_count: diff.changed_count } : null,
  }), [contentKey, effectiveRevId, diff, diffStrategy, queryKey, L.review_title, L.compare_vs_label, L.diff_changed_suffix, comparedRevLine, againstLabel]));

  return (
    <div className="flex items-center gap-2 min-w-0 py-1">
      <GitCompare className="w-4 h-4 shrink-0 text-primary" />
      <div className="flex-1 min-w-0">
        <div className={cn(text.title, 'truncate')}>{L.review_title ?? ''}</div>
        <div className={cn(text.muted, 'flex items-center gap-1.5 truncate')}>
          <span className="truncate">{comparedRevLine}</span>
          <span>{L.compare_vs_label ?? 'vs'}</span>
          <span className="truncate">{againstLabel}</span>
        </div>
      </div>
      {diff && (
        <div className={cn(text.value, 'shrink-0 tabular-nums')}>
          {diff.changed_count} {L.diff_changed_suffix ?? ''}
        </div>
      )}
    </div>
  );
}
